import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
  type DragStartEvent,
  type MeasuringConfiguration,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import type { SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import { useTableOrganization, useSaveTableOrganization } from '../hooks'
import { useMenuFilter } from '../hooks/useMenuFilter'
import { UNDO_TOAST_MS, useHiddenPaths } from '../hooks/useHiddenPages'
import { Path } from '../../../routes/paths'
import { useRowMoveAnimation } from '../hooks/useRowMoveAnimation'
import { useTableOrganizationStore } from '../stores/useTableOrganizationStore'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import {
  SIDEBAR_DRAG_MEASURING,
  listDragCollision,
  sidebarDragAnnouncements,
  sidebarListDragModifiers,
} from '../logic/dragInList'
import { buildTree, collectOrganizedTableIds } from '../utils/buildTree'
import { deleteFolderKeepingContents, moveFolderToContainer } from '../logic/folderMoves'
import { useToast } from '../../shared/components/Toast/ToastContext'
import {
  TOP_LEVEL_DROP_ID,
  folderDragCollision,
  tableDragCollision,
} from '../logic/tableListCollision'
import {
  filterFolderTree,
  filterFolderTreeByTable,
  lastOpenedMapFromTables,
  normalizeSearch,
  orderFolderTree,
  orderTables,
  sameLastOpened,
  tableMatchesSearch,
  type LastOpenedMap,
} from '../logic/tableOrder'
import FolderItem from './FolderItem'
import TableLink from './TableLink'
import FlatTableList from './FlatTableList'
import SidebarDragChip from './SidebarDragChip'
import { TableListActionsContext, type TableListActions } from './tableListActions'
import { KEEP_ORDER_STRATEGY, TableListRulesContext, type TableListRules } from './tableListRules'
import {
  ROOT_FLAT_TABLE_LABEL_LEFT,
  getChildRowInset,
  getFolderRowInset,
} from '../utils/rowGeometry'
import { useQueryErrorToast } from '@features/shared/hooks/useQueryErrorToast'
import type { FolderSchema, TableSidebarItem, TableSidebarTree } from '../api/contracts'

/** What's currently being dragged — drives the always-visible drag overlay. */
interface ActiveDrag {
  kind: 'table' | 'folder'
  label: string
  /** It's inside a folder, so the top level is somewhere it can go. */
  canLeaveFolder: boolean
}

interface OrganizedTableListProps {
  foldersEnabled?: boolean
  sort?: SidebarTableSort
  /** Narrows the list to tables (and folders) whose names contain it. */
  searchQuery?: string
  /** A table was chosen. Keep it stable: the list is memoized. */
  onSelect?: () => void
  /** Opens a table's settings panel. Keep it stable: the list is memoized. */
  onOpenTableSettings?: (tableId: string) => void
}

const EMPTY_ORGANIZATION: TableSidebarTree = {
  folders: [],
  unorganized_table_order: [],
  flat_table_order: [],
}

function cloneFolders(folders: FolderSchema[]) {
  return folders.map((folder) => ({
    ...folder,
    tables: folder.tables.map((tableRef) => ({ ...tableRef })),
  }))
}

function renumberFolderTables(folders: FolderSchema[]) {
  for (const folder of folders) {
    folder.tables.forEach((tableRef, index) => {
      tableRef.sort_order = index
    })
  }
}

function findTableContainer(folders: FolderSchema[], tableId: string): string | null {
  for (const folder of folders) {
    if (folder.tables.some((tableRef) => tableRef.table_id === tableId)) return folder.id
  }
  return null
}

function moveTableToContainer(
  organization: TableSidebarTree,
  tableId: string,
  targetFolderId: string | null,
  beforeTableId?: string
): TableSidebarTree {
  const folders = cloneFolders(organization.folders)
  let unorganizedOrder = [...organization.unorganized_table_order]
  const sourceFolderId = findTableContainer(organization.folders, tableId)

  // Read/write the ordered table-id list for a container (null = unorganized).
  const readList = (containerId: string | null): string[] =>
    containerId === null
      ? [...unorganizedOrder]
      : (folders.find((folder) => folder.id === containerId)?.tables.map((t) => t.table_id) ?? [])
  const writeList = (containerId: string | null, ids: string[]) => {
    if (containerId === null) {
      unorganizedOrder = ids
    } else {
      const target = folders.find((folder) => folder.id === containerId)
      if (target) target.tables = ids.map((id, index) => ({ table_id: id, sort_order: index }))
    }
  }

  if (sourceFolderId === targetFolderId) {
    // Same container: reorder with direction-aware arrayMove so dragging a row
    // down by exactly one position lands it after the hovered row (a plain
    // insert-before would re-insert it where it already was → no-op).
    const list = readList(targetFolderId)
    const from = list.indexOf(tableId)
    const to = beforeTableId ? list.indexOf(beforeTableId) : list.length - 1
    if (from === -1 || to === -1 || from === to) return organization
    writeList(targetFolderId, arrayMove(list, from, to))
  } else {
    // Cross-container: drop the row out of its source and insert it before the
    // hovered row in the target (or append when dropping onto the container).
    writeList(
      sourceFolderId,
      readList(sourceFolderId).filter((id) => id !== tableId)
    )
    const targetList = readList(targetFolderId).filter((id) => id !== tableId)
    const insertIndex = beforeTableId ? targetList.indexOf(beforeTableId) : -1
    if (insertIndex >= 0) targetList.splice(insertIndex, 0, tableId)
    else targetList.push(tableId)
    writeList(targetFolderId, targetList)
  }

  renumberFolderTables(folders)
  return { ...organization, folders, unorganized_table_order: unorganizedOrder }
}

function reorderSiblingFolders(
  organization: TableSidebarTree,
  activeFolderId: string,
  overFolderId: string
): TableSidebarTree {
  const folders = cloneFolders(organization.folders)
  const activeFolder = folders.find((folder) => folder.id === activeFolderId)
  const overFolder = folders.find((folder) => folder.id === overFolderId)
  if (!activeFolder || !overFolder || activeFolder.parent_id !== overFolder.parent_id) {
    return organization
  }

  const siblingIds = folders
    .filter((folder) => folder.parent_id === activeFolder.parent_id)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((folder) => folder.id)

  const oldIndex = siblingIds.indexOf(activeFolderId)
  const newIndex = siblingIds.indexOf(overFolderId)
  if (oldIndex === -1 || newIndex === -1) return organization

  const [moved] = siblingIds.splice(oldIndex, 1)
  siblingIds.splice(newIndex, 0, moved)

  return {
    ...organization,
    folders: folders.map((folder) => {
      const siblingIndex = siblingIds.indexOf(folder.id)
      return siblingIndex >= 0 ? { ...folder, sort_order: siblingIndex } : folder
    }),
  }
}

/** Cheap structural equality — avoids redundant preview updates during drag. */
function sameOrganization(a: TableSidebarTree, b: TableSidebarTree): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Which container a dragged table is over: a folder id, `null` for the top
 * level, or `undefined` when it isn't over anything that takes a table.
 */
function dropContainer(
  folders: FolderSchema[],
  activeTableId: string,
  overData: Record<string, unknown>
): { container: string | null; beforeTableId?: string } | undefined {
  if (overData.type === 'table') {
    const overTableId = overData.tableId as string
    if (overTableId === activeTableId) {
      return { container: findTableContainer(folders, activeTableId) }
    }
    return { container: findTableContainer(folders, overTableId), beforeTableId: overTableId }
  }
  if (overData.type === 'folder' || overData.type === 'folder-drop') {
    return { container: overData.folderId as string }
  }
  if (overData.type === 'unorganized-drop') return { container: null }
  return undefined
}

/**
 * The top level's tables, and where a table or folder dragged out of a folder
 * lands at the top level: while one is dragged it keeps room to drop on, even
 * when every table is in a folder. It lights up under a folder; a table shows
 * where it lands by moving there.
 */
function TopLevelDropZone({
  isDragging,
  isFolderDrag,
  children,
}: {
  isDragging: boolean
  isFolderDrag: boolean
  children: ReactNode
}) {
  // Registered here, inside the list's DndContext, so the drag can see it.
  const { setNodeRef, isOver } = useDroppable({
    id: TOP_LEVEL_DROP_ID,
    data: { type: 'unorganized-drop' },
  })

  return (
    <div
      ref={setNodeRef}
      className={`w-full min-w-0 space-y-[var(--sidebar-item-inset)] rounded-md transition-colors ${
        isDragging ? 'min-h-9' : ''
      } ${
        isFolderDrag && isOver
          ? 'bg-[color:var(--surface-info-subtle)] ring-1 ring-[color:var(--border-info)]'
          : ''
      }`}
    >
      {children}
    </div>
  )
}

const EMPTY_FOLDER_IDS: ReadonlySet<string> = new Set()

// Folders open and rows move between them mid-drag, so drop targets are
// measured throughout.
const TABLE_LIST_MEASURING: MeasuringConfiguration = {
  ...SIDEBAR_DRAG_MEASURING,
  droppable: { strategy: MeasuringStrategy.Always },
}

function OrganizedTableList({
  foldersEnabled = true,
  sort = 'custom',
  searchQuery = '',
  onSelect,
  onOpenTableSettings,
}: OrganizedTableListProps) {
  const { data: organization, isLoading: orgLoading, isError, error } = useTableOrganization()
  const tables = organization?.tables
  const saveMutation = useSaveTableOrganization()
  const expandFolders = useTableOrganizationStore((s) => s.expandFolders)
  const setRowDragActive = useGlobalSidebarStore((s) => s.setRowDragActive)
  // Once the drag ends, the final arrangement is rendered from here until the
  // save settles, so the dropped row doesn't flicker back to its origin.
  const [previewOrganization, setPreviewOrganization] = useState<TableSidebarTree | null>(null)
  const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null)
  // Folders collapsed while searching, for the query they were collapsed in.
  const [searchCollapse, setSearchCollapse] = useState<{
    query: string
    folderIds: ReadonlySet<string>
  }>({ query: '', folderIds: EMPTY_FOLDER_IDS })
  // Left-edge inset of the drag overlay: `start` is where the row began, `target`
  // is the indent of the slot under the cursor. The overlay's right edge stays
  // clamped while its left edge moves to match the target nesting level.
  const [targetInset, setTargetInset] = useState<number | null>(null)
  const dragStartInsetRef = useRef(0)
  // Where the chip starts: the dragged node's own left edge. A table's row is
  // indented to its level; a folder's node is its full-width wrapper (the row
  // inside it is indented), so it starts at the list's edge at any depth.
  const dragOriginInsetRef = useRef(0)
  const listRef = useRef<HTMLDivElement | null>(null)
  const navWidthRef = useRef<number | null>(null)
  // Snapshot of the tree at drag start (order materialized) — the base for the
  // final move computed on drop, and for deciding whether anything changed.
  const dragStartOrgRef = useRef<TableSidebarTree | null>(null)
  // True between dropping a moved row and its save settling — keeps the preview
  // visible across the handoff so the row never flickers back to its origin.
  const pendingCommitRef = useRef(false)
  const dragActiveRef = useRef(false)

  useQueryErrorToast(isError, error, 'loading tables')

  const isCustom = sort === 'custom'
  const normalizedQuery = normalizeSearch(searchQuery)
  const isSearching = normalizedQuery !== ''
  // The menu's hidden and favorites filters. The open table always shows; read
  // only while it's hidden, so other navigation doesn't re-render the list.
  const hiddenPaths = useHiddenPaths()
  const openHiddenTablePath = useGlobalSidebarStore((s) => {
    const path = s.activeTableId ? Path.tableRecords(s.activeTableId) : null
    return path && hiddenPaths.has(path) ? path : null
  })
  const tablePaths = useMemo(
    () => (tables ?? []).map((table) => Path.tableRecords(table.id)),
    [tables]
  )
  const { shows, showsEmptyFolders } = useMenuFilter('tables', tablePaths, {
    searching: isSearching,
    current: openHiddenTablePath,
  })
  const showsTable = useCallback((tableId: string) => shows(Path.tableRecords(tableId)), [shows])
  const committedOrganization = organization?.tree ?? EMPTY_ORGANIZATION
  const currentOrganization = previewOrganization ?? committedOrganization
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Recency follows `shownLastOpened`, which catches up with the live opens
  // only while the drawer isn't in use, then glides rows into place. Use is
  // read from the store without subscribing, so pointer moves re-render nothing.
  const liveLastOpened = useMemo(() => lastOpenedMapFromTables(tables ?? []), [tables])
  const [shownLastOpened, setShownLastOpened] = useState<LastOpenedMap | null>(() =>
    tables ? liveLastOpened : null
  )
  const lastOpened = shownLastOpened ?? liveLastOpened
  const { capture: captureRowPositions, play: playRowMoves } = useRowMoveAnimation(listRef)
  const latestRecencyRef = useRef({ live: liveLastOpened, sort })
  const recencyPendingRef = useRef(false)

  useLayoutEffect(() => {
    latestRecencyRef.current = { live: liveLastOpened, sort }
  })

  const showLiveRecency = useCallback(() => {
    recencyPendingRef.current = false
    if (latestRecencyRef.current.sort === 'recency') captureRowPositions()
    setShownLastOpened(latestRecencyRef.current.live)
  }, [captureRowPositions])

  useEffect(() => {
    if (!tables) return
    // The first data shows at once, even while the pointer is on the sidebar.
    if (shownLastOpened === null) {
      setShownLastOpened(liveLastOpened)
      return
    }
    if (sameLastOpened(shownLastOpened, liveLastOpened)) {
      recencyPendingRef.current = false
      return
    }
    if (useGlobalSidebarStore.getState().drawerInUse || dragActiveRef.current) {
      recencyPendingRef.current = true
      return
    }
    showLiveRecency()
  }, [tables, liveLastOpened, shownLastOpened, showLiveRecency])

  // A held re-sort shows as soon as the user stops using the drawer.
  useEffect(
    () =>
      useGlobalSidebarStore.subscribe((state, previous) => {
        if (
          previous.drawerInUse &&
          !state.drawerInUse &&
          recencyPendingRef.current &&
          !dragActiveRef.current
        ) {
          showLiveRecency()
        }
      }),
    [showLiveRecency]
  )

  useLayoutEffect(() => {
    playRowMoves()
  }, [shownLastOpened, playRowMoves])

  // While a row is dragged the drawer stays open, even if the pointer leaves it.
  const beginRowDrag = useCallback(() => {
    dragActiveRef.current = true
    setRowDragActive(true)
  }, [setRowDragActive])

  const finishRowDrag = useCallback(() => {
    dragActiveRef.current = false
    setRowDragActive(false)
    if (recencyPendingRef.current && !useGlobalSidebarStore.getState().drawerInUse) {
      showLiveRecency()
    }
  }, [setRowDragActive, showLiveRecency])

  useEffect(
    () => () => {
      if (dragActiveRef.current) useGlobalSidebarStore.getState().setRowDragActive(false)
    },
    []
  )

  const tablesById = useMemo<Map<string, TableSidebarItem>>(() => {
    const m = new Map<string, TableSidebarItem>()
    for (const table of tables ?? []) m.set(table.id, table)
    return m
  }, [tables])

  // The tree as arranged (Custom), which drags edit and depth lookups read.
  const folderNodes = useMemo(
    () => buildTree(currentOrganization.folders),
    [currentOrganization.folders]
  )

  const visibleFolderNodes = useMemo(
    () =>
      filterFolderTreeByTable(
        filterFolderTree(
          orderFolderTree(folderNodes, sort, tablesById, lastOpened),
          tablesById,
          normalizedQuery
        ),
        showsTable,
        showsEmptyFolders
      ),
    [folderNodes, sort, tablesById, lastOpened, normalizedQuery, showsTable, showsEmptyFolders]
  )

  const looseTables = useMemo(() => {
    if (!tables) return []
    const organizedIds = collectOrganizedTableIds(currentOrganization.folders)
    return tables.filter((table) => !organizedIds.has(table.id))
  }, [tables, currentOrganization.folders])

  // The top level's Custom order, with tables it doesn't list yet at the end.
  const customLooseTables = useMemo(
    () =>
      orderTables(looseTables, 'custom', {
        customOrder: currentOrganization.unorganized_table_order,
        lastOpened,
      }),
    [looseTables, currentOrganization.unorganized_table_order, lastOpened]
  )

  const visibleLooseTables = useMemo(
    () =>
      (isCustom ? customLooseTables : orderTables(looseTables, sort, { lastOpened })).filter(
        (table) => tableMatchesSearch(table, normalizedQuery) && showsTable(table.id)
      ),
    [isCustom, customLooseTables, looseTables, sort, lastOpened, normalizedQuery, showsTable]
  )

  const visibleFlatTables = useMemo(
    () =>
      orderTables(tables ?? [], sort, {
        customOrder: currentOrganization.flat_table_order,
        lastOpened,
      }).filter((table) => tableMatchesSearch(table, normalizedQuery) && showsTable(table.id)),
    [tables, sort, currentOrganization.flat_table_order, lastOpened, normalizedQuery, showsTable]
  )

  const rootFolderIds = useMemo(
    () => visibleFolderNodes.map((folder) => `folder-${folder.id}`),
    [visibleFolderNodes]
  )

  const looseTableIds = useMemo(
    () => visibleLooseTables.map((table) => `table-${table.id}`),
    [visibleLooseTables]
  )

  const folderDepthById = useMemo(() => {
    const map = new Map<string, number>()
    const walk = (nodes: typeof folderNodes, depth: number) => {
      for (const node of nodes) {
        map.set(node.id, depth)
        walk(node.children, depth + 1)
      }
    }
    walk(folderNodes, 0)
    return map
  }, [folderNodes])

  const collapsedWhileSearching =
    searchCollapse.query === normalizedQuery ? searchCollapse.folderIds : EMPTY_FOLDER_IDS

  const toggleFolderWhileSearching = useCallback(
    (folderId: string) =>
      setSearchCollapse((current) => {
        const folderIds = new Set(current.query === normalizedQuery ? current.folderIds : [])
        if (folderIds.has(folderId)) folderIds.delete(folderId)
        else folderIds.add(folderId)
        return { query: normalizedQuery, folderIds }
      }),
    [normalizedQuery]
  )

  const listRules = useMemo<TableListRules>(
    () => ({
      // Any sort moves a table into a folder or out of one; only Custom
      // reorders, since Name and Recency decide the order themselves.
      tableDragEnabled: !isSearching && (isCustom || currentOrganization.folders.length > 0),
      reorderEnabled: isCustom && !isSearching,
      folderCount: currentOrganization.folders.length,
      searchExpansion: isSearching
        ? {
            collapsedFolderIds: collapsedWhileSearching,
            toggleFolder: toggleFolderWhileSearching,
          }
        : null,
    }),
    [
      isSearching,
      isCustom,
      currentOrganization.folders.length,
      collapsedWhileSearching,
      toggleFolderWhileSearching,
    ]
  )

  // A rename builds on the newest tree (a move whose save hasn't settled
  // included), read when it's saved so the rows' actions stay stable.
  const renameBaseRef = useRef(currentOrganization)
  const commitRef = useRef<(nextOrganization: TableSidebarTree) => void>(() => {})
  useLayoutEffect(() => {
    renameBaseRef.current = currentOrganization
  })
  const { addToast } = useToast()
  const deleteFolder = useCallback(
    (folderId: string) => {
      const base = renameBaseRef.current
      const folder = base.folders.find((candidate) => candidate.id === folderId)
      if (!folder) return
      const parent = base.folders.find((candidate) => candidate.id === folder.parent_id)
      const holdsAnything =
        folder.tables.length > 0 || base.folders.some((child) => child.parent_id === folderId)
      commitRef.current(deleteFolderKeepingContents(base, folderId))
      addToast({
        type: 'info',
        title: `${folder.name} is deleted`,
        message: holdsAnything
          ? `What it held moved to ${parent ? parent.name : 'the top level'}.`
          : undefined,
        duration: UNDO_TOAST_MS,
        actions: [{ label: 'Undo', onClick: () => commitRef.current(base) }],
      })
    },
    [addToast]
  )
  const renameFolder = useCallback((folderId: string, name: string) => {
    const base = renameBaseRef.current
    commitRef.current({
      ...base,
      folders: base.folders.map((folder) =>
        folder.id === folderId ? { ...folder, name } : folder
      ),
    })
  }, [])

  const actions = useMemo<TableListActions>(
    () => ({ onSelect, openTableSettings: onOpenTableSettings, renameFolder, deleteFolder }),
    [onSelect, onOpenTableSettings, renameFolder, deleteFolder]
  )

  // A folder goes beside its siblings, into another folder, or to the top
  // level; a table to the nearest row or folder (see tableListCollision).
  // Either stops at the list's ends, and lands where it stopped.
  const hasTopLevelTables = visibleLooseTables.length > 0
  const collisionDetection = useMemo<CollisionDetection>(() => {
    const forFolder = folderDragCollision(currentOrganization.folders)
    const forTable = tableDragCollision(hasTopLevelTables)
    return listDragCollision((args) =>
      args.active.data.current?.type === 'folder' ? forFolder(args) : forTable(args)
    )
  }, [currentOrganization.folders, hasTopLevelTables])
  const dragModifiers = useMemo(() => sidebarListDragModifiers(() => listRef.current), [])

  const describeDragId = useCallback(
    (id: UniqueIdentifier) => {
      const value = String(id)
      if (value === TOP_LEVEL_DROP_ID) return 'the top level'
      if (value.startsWith('table-')) {
        return tablesById.get(value.slice('table-'.length))?.display_name_plural ?? 'a table'
      }
      const folderId = value.startsWith('folder-')
        ? value.slice('folder-'.length)
        : value.startsWith('drop-')
          ? value.slice('drop-'.length)
          : null
      const folder = currentOrganization.folders.find((candidate) => candidate.id === folderId)
      return folder ? `folder ${folder.name}` : 'a row'
    },
    [tablesById, currentOrganization.folders]
  )

  const accessibility = useMemo(
    () => ({ announcements: sidebarDragAnnouncements(describeDragId) }),
    [describeDragId]
  )

  // Left inset (marginLeft) a table row gets in a given container — 0 at root.
  const tableInsetForContainer = useCallback(
    (containerId: string | null) =>
      containerId === null ? 0 : getChildRowInset(folderDepthById.get(containerId) ?? 0),
    [folderDepthById]
  )

  const commitOrganization = useCallback(
    (nextOrganization: TableSidebarTree) => {
      pendingCommitRef.current = true
      setPreviewOrganization(nextOrganization)
      // Optimistic cache update + rollback-on-error live in the mutation hook.
      saveMutation.mutate(nextOrganization)
    },
    [saveMutation]
  )

  useLayoutEffect(() => {
    commitRef.current = commitOrganization
  })

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const data = event.active.data.current
      if (!data) return
      beginRowDrag()
      // Materialize the Custom order now so
      // cross-level moves and the final drop are precise; the top level's order
      // is otherwise lazy. Build on a drop whose save hasn't settled.
      const base = previewOrganization ?? committedOrganization
      const seed: TableSidebarTree = {
        folders: cloneFolders(base.folders),
        unorganized_table_order: customLooseTables.map((table) => table.id),
        flat_table_order: [...base.flat_table_order],
      }
      dragStartOrgRef.current = seed
      setPreviewOrganization(seed)
      navWidthRef.current = listRef.current?.getBoundingClientRect().width ?? null

      let startInset = 0
      if (data.type === 'table') {
        const container = findTableContainer(base.folders, data.tableId as string)
        startInset = tableInsetForContainer(container)
        const table = tablesById.get(data.tableId as string)
        setActiveDrag(
          table
            ? {
                kind: 'table',
                label: table.display_name_plural,
                canLeaveFolder: container !== null,
              }
            : null
        )
      } else if (data.type === 'folder') {
        startInset = getFolderRowInset(folderDepthById.get(data.folderId as string) ?? 0)
        const folder = base.folders.find((f) => f.id === data.folderId)
        setActiveDrag(
          folder
            ? { kind: 'folder', label: folder.name, canLeaveFolder: folder.parent_id !== null }
            : null
        )
      }
      dragStartInsetRef.current = startInset
      dragOriginInsetRef.current = data.type === 'table' ? startInset : 0
      setTargetInset(startInset)
    },
    [
      beginRowDrag,
      previewOrganization,
      committedOrganization,
      customLooseTables,
      tablesById,
      folderDepthById,
      tableInsetForContainer,
    ]
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      const activeData = active.data.current
      const overData = over?.data.current
      if (!activeData) return

      if (!overData) {
        // Over nothing to drop beside, letting go puts the row back, so show it there.
        setTargetInset(dragStartInsetRef.current)
        if (dragStartOrgRef.current) setPreviewOrganization(dragStartOrgRef.current)
        return
      }

      if (activeData.type === 'folder') {
        // Nothing moves until the drop (the sort strategy makes room beside a
        // sibling); the overlay takes the level the folder would land at.
        const nextInset =
          overData.type === 'folder-drop'
            ? getFolderRowInset((folderDepthById.get(overData.folderId as string) ?? 0) + 1)
            : overData.type === 'unorganized-drop'
              ? getFolderRowInset(0)
              : dragStartInsetRef.current
        setTargetInset((current) => (current === nextInset ? current : nextInset))
        return
      }
      if (activeData.type !== 'table') return

      // Auto-expand a collapsed folder under the cursor so its contents become
      // reachable drop targets (file-explorer style).
      const overFolderId =
        overData.type === 'folder' || overData.type === 'folder-drop'
          ? (overData.folderId as string)
          : null
      if (
        overFolderId &&
        !useTableOrganizationStore.getState().expandedFolderIds.includes(overFolderId)
      ) {
        expandFolders([overFolderId])
      }
      const activeTableId = activeData.tableId as string
      const base = previewOrganization ?? committedOrganization

      // Where would the row drop right now?
      const target = dropContainer(base.folders, activeTableId, overData)
      if (!target) return
      const sourceContainer = findTableContainer(base.folders, activeTableId)

      // Resize the overlay's left edge to match that level (folders keep their
      // own level). This part is independent of any data move.
      const nextInset = tableInsetForContainer(target.container)
      setTargetInset((current) => (current === nextInset ? current : nextInset))

      // Only move data across LEVELS — that's what opens a drop slot in the new
      // container. Same-level reordering is left to the sort strategy (animated,
      // and free of the data/strategy fight that snapped downward moves back).
      if (target.container !== sourceContainer) {
        // Under Name or Recency the table joins the end of its new place's
        // Custom order; the sort decides where it shows.
        const next = moveTableToContainer(
          base,
          activeTableId,
          target.container,
          isCustom ? target.beforeTableId : undefined
        )
        if (!sameOrganization(next, base)) setPreviewOrganization(next)
      }
    },
    [
      committedOrganization,
      previewOrganization,
      expandFolders,
      tableInsetForContainer,
      folderDepthById,
      isCustom,
    ]
  )

  const resetDrag = useCallback(() => {
    dragStartOrgRef.current = null
    setActiveDrag(null)
    setTargetInset(null)
    finishRowDrag()
  }, [finishRowDrag])

  const handleDragCancel = useCallback(() => {
    resetDrag()
    pendingCommitRef.current = false
    setPreviewOrganization(null)
  }, [resetDrag])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      // Let go over nothing to drop beside: the row goes back where it was.
      if (!over) {
        handleDragCancel()
        return
      }

      // Preview holds cross-level moves; resolve the final same-level slot here.
      const startOrg = dragStartOrgRef.current
      const base = previewOrganization ?? startOrg
      const activeData = active.data.current
      const overData = over.data.current

      let next = base
      if (base && overData && activeData?.type === 'table') {
        const activeTableId = activeData.tableId as string
        const target = dropContainer(base.folders, activeTableId, overData)
        if (!isCustom) {
          // Only into a folder or out of one: beside its neighbors it stays put.
          if (target && target.container !== findTableContainer(base.folders, activeTableId)) {
            next = moveTableToContainer(base, activeTableId, target.container)
          }
        } else if (target && overData.tableId !== activeTableId) {
          next = moveTableToContainer(base, activeTableId, target.container, target.beforeTableId)
        }
      } else if (base && overData && activeData?.type === 'folder') {
        const folderId = activeData.folderId as string
        if (overData.type === 'folder') {
          next = reorderSiblingFolders(base, folderId, overData.folderId as string)
        } else if (overData.type === 'folder-drop') {
          const parentId = overData.folderId as string
          next = moveFolderToContainer(base, folderId, parentId)
          // Opened, so the folder shows where it went.
          if (next !== base) expandFolders([parentId])
        } else if (overData.type === 'unorganized-drop') {
          next = moveFolderToContainer(base, folderId, null)
        }
      }

      resetDrag()

      if (startOrg && next && !sameOrganization(next, startOrg)) {
        commitOrganization(next)
      } else {
        setPreviewOrganization(null)
      }
    },
    [previewOrganization, commitOrganization, handleDragCancel, resetDrag, expandFolders, isCustom]
  )

  // Once a moved row's save settles, the committed cache holds the new (or
  // rolled-back) arrangement, so the preview can be dropped without any visible
  // change.
  useEffect(() => {
    if (pendingCommitRef.current && !saveMutation.isPending) {
      pendingCommitRef.current = false
      setPreviewOrganization(null)
    }
  }, [saveMutation.isPending])

  const handleFlatReorder = useCallback(
    (activeTableId: string, overTableId: string) => {
      // Build on a reorder whose save hasn't settled yet, not the older cache.
      const base = previewOrganization ?? committedOrganization
      const ids = orderTables(tables ?? [], 'custom', {
        customOrder: base.flat_table_order,
        lastOpened,
      }).map((table) => table.id)
      const from = ids.indexOf(activeTableId)
      const to = ids.indexOf(overTableId)
      if (from === -1 || to === -1 || from === to) return
      commitOrganization({ ...base, flat_table_order: arrayMove(ids, from, to) })
    },
    [tables, previewOrganization, committedOrganization, lastOpened, commitOrganization]
  )

  if (orgLoading) {
    return (
      <div className="space-y-[var(--sidebar-item-inset)] p-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className="h-[var(--sidebar-tile-size)] animate-pulse rounded bg-muted"
          />
        ))}
      </div>
    )
  }

  if (isError || !tables) {
    return (
      <div className="p-3 text-xs text-[color:var(--sidebar-submenu-text-faint)]">
        Tables couldn't be loaded.
      </div>
    )
  }

  const nothingShown =
    tables.length > 0 &&
    (foldersEnabled
      ? visibleFolderNodes.length === 0 && visibleLooseTables.length === 0
      : visibleFlatTables.length === 0)
  if (nothingShown) {
    return (
      <p className="px-3 py-2 text-sm text-[color:var(--sidebar-submenu-text-faint)]">
        {isSearching ? `No tables match "${searchQuery.trim()}"` : 'No tables to show'}
      </p>
    )
  }

  const rowStrategy = listRules.reorderEnabled ? verticalListSortingStrategy : KEEP_ORDER_STRATEGY

  if (!foldersEnabled) {
    return (
      <TableListActionsContext.Provider value={actions}>
        <div ref={listRef} className="w-full min-w-0">
          <FlatTableList
            tables={visibleFlatTables}
            reorderEnabled={isCustom && !isSearching}
            onReorder={handleFlatReorder}
            onDragBegin={beginRowDrag}
            onDragFinish={finishRowDrag}
          />
        </div>
      </TableListActionsContext.Provider>
    )
  }

  return (
    <TableListActionsContext.Provider value={actions}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        modifiers={dragModifiers}
        measuring={TABLE_LIST_MEASURING}
        accessibility={accessibility}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <TableListRulesContext.Provider value={listRules}>
          <div ref={listRef} className="w-full min-w-0">
            <nav
              className="w-full min-w-0 space-y-[var(--sidebar-item-inset)]"
              data-tour="sidebar-tables"
            >
              <SortableContext items={rootFolderIds} strategy={rowStrategy}>
                {visibleFolderNodes.map((folder) => (
                  <FolderItem
                    key={folder.id}
                    folder={folder}
                    tablesById={tablesById}
                    depth={0}
                    siblingFolderCount={visibleFolderNodes.length}
                  />
                ))}
              </SortableContext>

              <TopLevelDropZone
                isDragging={activeDrag?.canLeaveFolder ?? false}
                isFolderDrag={activeDrag?.kind === 'folder'}
              >
                <SortableContext items={looseTableIds} strategy={rowStrategy}>
                  {visibleLooseTables.map((table) => (
                    <TableLink
                      key={table.id}
                      table={table}
                      labelInset={ROOT_FLAT_TABLE_LABEL_LEFT}
                      sortableId={listRules.tableDragEnabled ? `table-${table.id}` : undefined}
                    />
                  ))}
                </SortableContext>
              </TopLevelDropZone>
            </nav>
          </div>
        </TableListRulesContext.Provider>

        {/* Floating drag chip: clamped to the vertical axis (right edge fixed) and
            to the list's ends (sidebarListDragModifiers), and
            resized on the left so its width matches the drop target's nesting. */}
        <DragOverlay dropAnimation={null} modifiers={dragModifiers}>
          {activeDrag ? (
            <SidebarDragChip
              kind={activeDrag.kind}
              label={activeDrag.label}
              style={{
                marginLeft: (targetInset ?? dragStartInsetRef.current) - dragOriginInsetRef.current,
                width: navWidthRef.current
                  ? navWidthRef.current - (targetInset ?? dragStartInsetRef.current)
                  : undefined,
              }}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </TableListActionsContext.Provider>
  )
}

export default memo(OrganizedTableList)
